import React, { FC, useState, useEffect } from 'react';
import Select, { components } from 'react-select';
import { SelectComponentsProps } from 'react-select/src/Select';
import CreatableSelect from 'react-select/creatable';
import Form from 'react-bootstrap/Form';
import styled from 'styled-components';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { ISelectProps } from './types';
import ValueContainer from './MultiSelectValueContainer';

interface IMultiSelectProps extends ISelectProps {
  showTotal?: boolean;
  allowSelectAll?: boolean;
  onMenuClose?: () => void;
}

/*
 * Abstracted select component through `react-select`.
 * Used when multi select.
 */
const MultiSelect: FC<IMultiSelectProps> = ({
  placeholder,
  isClearable,
  isCreatable,
  isDisabled,
  isSearchable,
  options,
  noOptionsMessage,
  createMessage,
  disableOptions,
  showTotal,
  allowSelectAll,
  name,
  limitOptions,
  onMenuClose,
  onChange,
  ...restProps
}) => {
  const { t } = useTranslation();

  const [isAllSelected, setIsAllSelected] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState('');

  const { setValue, trigger } = useFormContext();

  useEffect(() => {
    if (options) {
      setSearchTerm('');
    }
  }, [options]);

  const toggleSelectAll = () => {
    const hasGroupedOptions = options ? options.filter((opt: any) => opt.options).length > 0 : false;
    const groupedOptions = hasGroupedOptions ? options!.map((opt: any) => opt.options).flat() : [];
    const selectOptions = hasGroupedOptions ? groupedOptions : options;
    const allSelected = isAllSelected;

    const newValue = allSelected ? [] : selectOptions;

    setValue(name!, newValue);
    setIsAllSelected(!allSelected);

    if (onChange) {
      onChange(newValue as any);
    }

    trigger(name);
  };

  // Customize MenuList
  const MenuList = (menuListProps: any) => {
    const hasGroupedOptions = menuListProps.options.filter((opt: any) => opt.options).length > 0;
    return (
      <>
        <components.MenuList {...menuListProps}>{menuListProps.children}</components.MenuList>

        {allowSelectAll && (hasGroupedOptions || menuListProps.options.length > 1) && (
          <OptionButton onClick={toggleSelectAll}>
            <SelectAllBtn>{isAllSelected ? t('form.removeAll') : t('form.selectAll')}</SelectAllBtn>
          </OptionButton>
        )}
      </>
    );
  };

  // Customize option
  const Option = (optionProps: any) => {
    const { label, isSelected } = optionProps;
    const isCreateBtn =
      isCreatable && typeof label === 'object' && label.props?.children[0]?.includes(t('form.create'));

    if (isCreateBtn)
      return (
        <OptionButton>
          <components.Option {...optionProps}>{label}</components.Option>
        </OptionButton>
      );

    return (
      <components.Option {...optionProps}>
        <label
          htmlFor={label}
          className="mb-1 mt-1"
        >
          {label}
        </label>
        <Form.Check
          custom
          type="checkbox"
          name={label}
          id={label}
          value={label}
          checked={isSelected}
        />
      </components.Option>
    );
  };

  // Customize multi value
  const MultiValue = (multiValueProps: any) => {
    const { data } = multiValueProps;
    return (
      <components.MultiValue {...multiValueProps}>
        <span>{data.label}</span>
      </components.MultiValue>
    );
  };

  // Format of group labels, which wrap options
  const formatGroupLabel = (data: any) => (
    <Group>
      <GroupLabel>{data.label}</GroupLabel>
      <GroupBadge>{data.options.length}</GroupBadge>
    </Group>
  );

  const filterOption = (option: any) => {
    if (!searchTerm) {
      return true;
    }
    return (
      option.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (option.label_en && option.label_en.toLowerCase().includes(searchTerm.toLowerCase()))
    );
  };

  // Render options and add `allOption` if needed
  const renderOptions = () => {
    let selectOptions = options?.slice();

    const limit = process.env.REACT_APP_OPTION_LIMIT ? parseInt(process.env.REACT_APP_OPTION_LIMIT, 10) : 100;

    if (limitOptions && selectOptions) {
      selectOptions = selectOptions.filter(option => filterOption(option)).slice(0, limit);
    } else if (selectOptions) {
      selectOptions = selectOptions.filter(option => filterOption(option));
    }

    return selectOptions;
  };

  // Handle input change
  const handleInputChange = (inputValue: string) => {
    setSearchTerm(inputValue);
    return inputValue;
  };

  // Set value and trigger validation manually
  const customOnChange = (selected: any) => {
    setValue(name!, selected);
    trigger(name);
    if (onChange) {
      onChange(selected);
    }
  };

  const handleOnChange = (selected: any, event: any) => {
    let selectedTest = selected;

    // Transform empty array to null, because validation `required` is not triggered
    if (Array.isArray(selectedTest) && !selectedTest.length) selectedTest = null;

    // When `Select/Deselect all` scenario
    if (allowSelectAll) {
      // First flatten the options in case there are grouped options
      const flattenOptions = options!.map(o => o.options || o).flat();

      // When `Select/Deselect all` selected
      const isAllClicked = event.action === 'select-option' && event.option.value === '*';

      if (isAllClicked) {
        // Select all options
        if (!isAllSelected) {
          setIsAllSelected(true);
          return customOnChange(flattenOptions);
        }
        // Deselect all options
        setIsAllSelected(false);
        return customOnChange(null);
      }

      // Check in case the user select all options manually
      if (Array.isArray(selectedTest) && selectedTest!.length === flattenOptions.length) {
        setIsAllSelected(true);
      } else {
        setIsAllSelected(false);
      }

      // Single selection
      return customOnChange(selectedTest);
    }

    // All other scenarios
    return customOnChange(selectedTest);
  };

  const componentProps: SelectComponentsProps = {
    ...restProps,
    className: 'c-custom-select multi-dropdown',
    classNamePrefix: 'cs',
    placeholder: placeholder || t('form.select'),
    isDisabled,
    isClearable,
    isSearchable,
    components: { Option, MultiValue, MenuList, ValueContainer },
    hideSelectedOptions: false,
    options: renderOptions(),
    onInputChange: handleInputChange,
    isOptionDisabled: (option: any) => {
      return disableOptions || option.isDisabled;
    },
    noOptionsMessage: () => noOptionsMessage || t('form.noOptions'),
    isMulti: true,
    closeMenuOnSelect: false,
    showTotal,
    formatGroupLabel,
    allowSelectAll,
    onChange: handleOnChange,
    onMenuClose,
    filterOption: null,
    theme: (theme: any) => ({
      ...theme,
      colors: {
        ...theme.colors,
        primary: '#32bfa3',
        primary25: '#f2f3f8',
        primary50: '#e8e9ef',
        neutral50: '#ced4da',
      },
    }),
  };

  if (isCreatable)
    return (
      <CreatableSelect
        {...componentProps}
        formatCreateLabel={(label: string) => (
          <span>
            {`${createMessage || t('form.create')} `}
            <span className="font-weight-bold">{label}</span>
          </span>
        )}
      />
    );

  return <Select {...componentProps} />;
};

const OptionButton = styled.div`
  color: ${props => props.theme.colors.primary};
  border-top: ${props => `1px solid ${props.theme.colors.gray}`};
  cursor: pointer;

  .cs__option {
    justify-content: center !important;
  }
`;

const SelectAllBtn = styled.div`
  padding: 0.75rem 1rem;
  text-align: center;
`;

const Group = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: ${props => props.theme.colors.dark};
`;

const GroupLabel = styled.span`
  font-weight: 800;
`;

const GroupBadge = styled.span`
  background: ${props => props.theme.colors.lighter};
  border-radius: 2em;
  display: inline-block;
  padding: 0.325rem;
  text-align: center;
`;

export default MultiSelect;
